import type { FeedbackScreenshotLink } from "@esi/shared";
import { ExternalLink, Image } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";

/**
 * A report's screenshot in the console (openspec change `in-app-feedback`, design D4): whether
 * the reporter showed any content region, and a button that mints a 60-second URL (audited as
 * `feedback.screenshot_read`) and opens it in a new tab. The console's own policy never loads
 * tenant objects; a top-level navigation to the inline PNG does not need it to.
 */
export function FeedbackScreenshot(props: { reportId: string; unmasked: boolean }) {
  const { t } = useTranslation();
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState<"expired" | "failed" | "blocked" | null>(null);
  const open = async () => {
    // Opened now, within the click, so no popup blocker stops it; pointed at the URL once minted.
    const tab = window.open("", "_blank");
    if (!tab) {
      // A blocked pop-up: never navigate the console itself away.
      setFailed("blocked");
      return;
    }
    tab.opener = null;
    setOpening(true);
    setFailed(null);
    try {
      const link: FeedbackScreenshotLink = await ok(
        await api.api.operator.feedback[":reportId"].screenshot.$get({
          param: { reportId: props.reportId },
        }),
      );
      tab.location.href = link.url;
    } catch (error) {
      tab.close();
      setFailed(error instanceof ApiError && error.status === 404 ? "expired" : "failed");
    } finally {
      setOpening(false);
    }
  };
  return (
    <section aria-labelledby="feedback-screenshot" className="flex flex-col gap-2">
      <h2 id="feedback-screenshot" className="text-sm font-semibold text-fg">
        {t("feedbackScreenshot.label")}
      </h2>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface-raised p-3">
        <span className="flex min-w-0 items-center gap-2 text-sm text-fg-secondary">
          <Image aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
          {props.unmasked
            ? t("feedbackScreenshot.console.unmasked")
            : t("feedbackScreenshot.console.masked")}
        </span>
        <Button type="button" variant="outline" size="sm" disabled={opening} onClick={open}>
          <ExternalLink aria-hidden="true" strokeWidth={1.75} />
          {opening ? t("feedbackScreenshot.console.opening") : t("feedbackScreenshot.console.open")}
        </Button>
      </div>
      {failed ? (
        <p role="alert" className="text-xs text-danger-fg">
          {t(`feedbackScreenshot.console.${failed}`)}
        </p>
      ) : null}
    </section>
  );
}
